/**
 * useGhcpModels Hook
 *
 * Fetches available models for a GitHub Copilot agent connection through the
 * data-plane front door (`.../connections/{name}/models`), which authenticates
 * the portal token and proxies to the runtime. The runtime forwards through
 * Token Broker, which obtains the connection token from CRP before calling
 * GitHub Copilot.
 *
 * On failure the dropdown is empty but the combobox still accepts freeform
 * input (same behaviour as OpenAI).
 */

import { useQuery } from '@tanstack/react-query';
import { useAppContextBuilder } from '@/hooks/useConnectionMutations';
import { useAzureResourcePickerIdentity } from '@/hooks/useAzureResourcePicker';
import { listConnectionModels, type GhcpModel } from '@/services/connectionApi';

// =============================================================================
// HOOK
// =============================================================================

export interface UseGhcpModelsReturn {
  models: GhcpModel[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch GHCP models for a given connection through the data plane. On failure
 * the error is surfaced to the caller (shown as a validation error on the
 * field) and the model list is empty — the user can still type any model name
 * via freeform input.
 */
export function useGhcpModels(
  connectionName: string,
  enabled = true,
): UseGhcpModelsReturn {
  const { buildContext, appId } = useAppContextBuilder();
  // Scope the cache to the signed-in tenant/account so switching tenants can't reuse the
  // previous tenant's catalog for the same project/app/connection names.
  const identity = useAzureResourcePickerIdentity();

  const query = useQuery<GhcpModel[], Error>({
    queryKey: ['ghcp-models', identity, appId, connectionName],
    queryFn: async () => {
      const ctx = await buildContext();
      // `enabled` already gates on appId, so a null context here means the
      // access-token acquisition failed rather than a missing app.
      if (!ctx) throw new Error('Failed to get access token');

      return listConnectionModels(ctx, connectionName);
    },
    enabled: enabled && !!connectionName && !!appId,
    // A freshly created draft connection may not be persisted to the runtime yet, so the first
    // /models call can 404, which listConnectionModels degrades to an empty list. Don't cache an
    // empty result as fresh — otherwise the picker stays empty for 5 min after the draft persists.
    // A populated catalog is cached for 5 min; an empty one stays stale so it refetches on remount.
    staleTime: (query) => (query.state.data && query.state.data.length > 0 ? 5 * 60 * 1000 : 0),
    refetchOnMount: true,
    retry: (failureCount, error) => {
      // Deterministic client errors (401/403 permission, 404 route-not-deployed, 400) won't
      // succeed on retry — only retry transient (5xx / network) failures.
      const status = (error as { status?: number }).status;
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  return {
    models: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}
